/**
 * Form modal for creating new OPL issues.
 */

import { useState } from 'react';
import { Form, Input, Modal, Select, message } from 'antd';
import type { OplIssueCreate } from '@/types';
import * as oplApi from '@/api/opl';
import RichTextEditor, { isContentEmpty } from '@/components/common/RichTextEditor';
import UserSelector from '@/components/opl/UserSelector';

interface OplIssueFormProps {
  open: boolean;
  projectId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const OplIssueForm: React.FC<OplIssueFormProps> = ({
  open,
  projectId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState('');
  const [imageUploading, setImageUploading] = useState(false);

  const handleSubmit = async () => {
    if (isContentEmpty(description)) {
      message.error('问题正文不能为空');
      return;
    }
    if (imageUploading) {
      message.warning('图片上传中，请稍候...');
      return;
    }
    try {
      const values = await form.validateFields();
      setLoading(true);

      const data: OplIssueCreate = {
        description,
        bsw_version: values.bsw_version || undefined,
        priority: values.priority || undefined,
        impact: values.impact || undefined,
        owner_id: values.owner_id || undefined,
      };

      await oplApi.createIssue(projectId, data);
      message.success('问题创建成功');
      form.resetFields();
      setDescription('');
      onSuccess();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown[] })?.errorFields) return;
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || '创建失败';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="新建问题"
      open={open}
      onOk={handleSubmit}
      onCancel={() => {
        form.resetFields();
        setDescription('');
        onClose();
      }}
      confirmLoading={loading}
      okText="创建"
      cancelText="取消"
      width={700}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item label="问题描述" required>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="请输入问题描述... 支持粘贴图片和超链接"
            onUploadingChange={setImageUploading}
          />
        </Form.Item>
        <Form.Item name="bsw_version" label="底软版本">
          <Input placeholder="底软版本（选填）" />
        </Form.Item>
        <Form.Item name="priority" label="优先级">
          <Select
            allowClear
            placeholder="选择优先级（选填）"
            options={[
              { value: 'p0', label: 'P0-紧急' },
              { value: 'p1', label: 'P1-高' },
              { value: 'p2', label: 'P2-中' },
              { value: 'p3', label: 'P3-低' },
            ]}
          />
        </Form.Item>
        <Form.Item name="impact" label="问题影响">
          <Input.TextArea rows={2} placeholder="问题影响（选填）" />
        </Form.Item>
        <Form.Item name="owner_id" label="负责人">
          <UserSelector placeholder="选择负责人（选填）" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default OplIssueForm;
